/**
 * @description Authentication context for Lynx Admin portal
 *
 * Key features:
 * - Token storage: Access token in sessionStorage, refresh token in httpOnly cookie
 * - Auto-refresh: Automatically refreshes token when expired or on 401
 * - Session persistence: Checks for existing session on mount
 * - User context: Provides authenticated admin user info
 * - Role enforcement: Rejects non-superadmin users at frontend level
 *
 * @dependencies
 * - apiClient: HTTP requests to backend
 * - Backend auth endpoints: /api/auth/me, /api/auth/refresh, /api/auth/logout
 *
 * @notes
 * - Adapted from merchant frontend AuthContext (same auth backend)
 * - Admin users must have role 'superadmin' — others are rejected on fetchUserInfo
 * - Backend also enforces superadmin via requireSuperAdmin middleware on /api/admin/*
 * - Access tokens stored in sessionStorage (survives refresh, not tab close)
 * - Refresh tokens are in httpOnly cookies (handled by browser)
 * - Token refresh happens automatically on 401 responses
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../services/api/apiClient';

const AuthContext = createContext(null);

const TOKEN_EXPIRY_BUFFER_MS = 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);

  const initializingRef = useRef(false);
  const refreshPromiseRef = useRef(null);

  const storeToken = useCallback((token, expiresIn) => {
    setAccessToken(token);
    const expiresAt = Date.now() + (expiresIn * 1000);
    setTokenExpiresAt(expiresAt);
    sessionStorage.setItem('access_token', token);
    sessionStorage.setItem('token_expires_at', expiresAt.toString());
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }, []);

  const clearAuthState = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    setTokenExpiresAt(null);
    setIsSignedIn(false);
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('token_expires_at');
    sessionStorage.removeItem('wristband_auth_request_token');
    delete apiClient.defaults.headers.common['Authorization'];
  }, []);

  /**
   * Fetch user info from /api/auth/me
   * Enforces superadmin role — rejects non-admin users at frontend level
   * (Backend also enforces via requireSuperAdmin middleware on /api/admin/*)
   */
  const fetchUserInfo = useCallback(async () => {
    try {
      const response = await apiClient.get('/auth/me');
      if (response.data.success) {
        const userData = response.data.user;

        // Defense in depth: reject non-superadmin users at frontend level
        if (userData.role !== 'superadmin') {
          console.error('Access denied: admin portal requires superadmin role');
          return false;
        }

        setUser(userData);
        setIsSignedIn(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      return false;
    }
  }, []);

  const refreshToken = useCallback(async () => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    refreshPromiseRef.current = (async () => {
      try {
        const response = await apiClient.post('/auth/refresh');
        if (response.data.success && response.data.accessToken) {
          storeToken(response.data.accessToken, response.data.expiresIn);
          return true;
        }
        return false;
      } catch (error) {
        console.error('Token refresh failed:', error);
        clearAuthState();
        return false;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  }, [storeToken, clearAuthState]);

  const isTokenExpired = useCallback(() => {
    if (!tokenExpiresAt) return true;
    return Date.now() >= (tokenExpiresAt - TOKEN_EXPIRY_BUFFER_MS);
  }, [tokenExpiresAt]);

  const getToken = useCallback(async () => {
    if (accessToken && !isTokenExpired()) {
      return accessToken;
    }

    const storedToken = sessionStorage.getItem('access_token');
    const storedExpiresAt = sessionStorage.getItem('token_expires_at');

    if (storedToken && storedExpiresAt) {
      const expiresAt = parseInt(storedExpiresAt, 10);
      if (Date.now() < expiresAt - TOKEN_EXPIRY_BUFFER_MS) {
        setAccessToken(storedToken);
        setTokenExpiresAt(expiresAt);
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        return storedToken;
      }
    }

    const refreshed = await refreshToken();
    if (refreshed) {
      return sessionStorage.getItem('access_token');
    }

    return null;
  }, [accessToken, isTokenExpired, refreshToken]);

  const handleAuthCallback = useCallback(async (token, expiresIn) => {
    try {
      storeToken(token, expiresIn);
      const success = await fetchUserInfo();

      if (success) {
        setIsLoaded(true);
        return true;
      }

      clearAuthState();
      setIsLoaded(true);
      return false;
    } catch (error) {
      console.error('Auth callback error:', error);
      clearAuthState();
      setIsLoaded(true);
      return false;
    }
  }, [storeToken, fetchUserInfo, clearAuthState]);

  const signOut = useCallback(async () => {
    try {
      const response = await apiClient.post('/auth/logout');
      const logoutUrl = response.data.logoutUrl;

      if (logoutUrl) {
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('token_expires_at');
        sessionStorage.removeItem('wristband_auth_request_token');
        window.location.href = logoutUrl;
      } else {
        clearAuthState();
        window.location.href = '/sign-in';
      }
    } catch (error) {
      clearAuthState();
      window.location.href = '/sign-in';
    }
  }, [clearAuthState]);

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      if (initializingRef.current) return;
      initializingRef.current = true;

      try {
        const storedToken = sessionStorage.getItem('access_token');
        const storedExpiresAt = sessionStorage.getItem('token_expires_at');

        if (storedToken && storedExpiresAt) {
          const expiresAt = parseInt(storedExpiresAt, 10);
          if (Date.now() < expiresAt - TOKEN_EXPIRY_BUFFER_MS) {
            setAccessToken(storedToken);
            setTokenExpiresAt(expiresAt);
            apiClient.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
            const success = await fetchUserInfo();
            if (success) {
              setIsLoaded(true);
              initializingRef.current = false;
              return;
            }
          }
        }

        const refreshed = await refreshToken();
        if (refreshed) {
          const success = await fetchUserInfo();
          if (success) {
            setIsLoaded(true);
            initializingRef.current = false;
            return;
          }
        }

        clearAuthState();
        setIsLoaded(true);
        initializingRef.current = false;
      } catch (error) {
        console.error('Auth initialization error:', error);
        clearAuthState();
        setIsLoaded(true);
        initializingRef.current = false;
      }
    };

    initializeAuth();
  }, [fetchUserInfo, refreshToken, clearAuthState]);

  // 401 interceptor for automatic token refresh
  useEffect(() => {
    const interceptor = apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes('/auth/')
        ) {
          originalRequest._retry = true;
          const refreshed = await refreshToken();

          if (refreshed) {
            const newToken = sessionStorage.getItem('access_token');
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return apiClient(originalRequest);
          }

          clearAuthState();
        }

        return Promise.reject(error);
      }
    );

    return () => {
      apiClient.interceptors.response.eject(interceptor);
    };
  }, [refreshToken, clearAuthState]);

  const value = {
    user,
    isSignedIn,
    isLoaded,
    getToken,
    signOut,
    handleAuthCallback,
    accessToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return {
    isSignedIn: context.isSignedIn,
    isLoaded: context.isLoaded,
    userId: context.user?.id || null,
    getToken: context.getToken,
    signOut: context.signOut,
  };
}

export function useUser() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useUser must be used within an AuthProvider');
  }

  return {
    isSignedIn: context.isSignedIn,
    isLoaded: context.isLoaded,
    user: context.user,
  };
}

export { AuthContext };
export default AuthContext;
